import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Fade,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Schedule,
    AccessTime,
    TrendingUp,
    PauseCircleOutline
} from '@mui/icons-material';

const InvestmentCountdown = ({ operationalSettings }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const [isInvestmentOpen, setIsInvestmentOpen] = useState(false);
    const [countdownActive, setCountdownActive] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    
    useEffect(() => {
        if (operationalSettings?.useInvestmentOpenTime == 0) {
            setIsInvestmentOpen(true);
            setStatusMessage('Investment is always available');
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            
            // Parse the full date strings directly
            const openTime = new Date(operationalSettings.invOpenHour);
            const closeTime = new Date(operationalSettings.invCloseHour);
            
            // Check if the dates are valid
            if (isNaN(openTime.getTime()) || isNaN(closeTime.getTime())) {
                setIsInvestmentOpen(false);
                setCountdownActive(false);
                setStatusMessage('Invalid time configuration');
                return;
            }
            
            // If close time is earlier than open time, it means it spans to next day
            if (closeTime <= openTime) {
                closeTime.setDate(closeTime.getDate() + 1);
            }

            // Compare current time with the parsed dates
            if (now >= openTime && now < closeTime) {
                // Investment is currently open - countdown to close time
                setIsInvestmentOpen(true);
                setCountdownActive(true);
                setStatusMessage('Investment is currently open');
                
                const timeDiff = closeTime - now;
                const hours = Math.floor(timeDiff / (1000 * 60 * 60));
                const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
                
                setTimeLeft({ hours, minutes, seconds });
            } else {
                // Investment is closed (either before opening time or after closing time)
                setIsInvestmentOpen(false);
                setCountdownActive(false);
                setStatusMessage('Investment is currently closed');
                setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [operationalSettings]);

    const formatTime = (value) => {
        return value.toString().padStart(2, '0');
    };

    const getTooltipMessage = () => {
        if (operationalSettings?.useInvestmentOpenTime == 0) {
            return 'Investment is always available - no time restrictions';
        }
        
        if (isInvestmentOpen && countdownActive) {
            return 'Investment is currently open. Trading is allowed during this time.';
        } else {
            return 'Investment is currently closed. Please wait for the opening time.';
        }
    };

    const getStatusColor = () => {
        if (operationalSettings?.useInvestmentOpenTime == 0) {
            return '#10b981'; // green
        }
        
        if (isInvestmentOpen && countdownActive) {
            return '#10b981'; // green
        } else {
            return '#ef4444'; // red
        }
    };

    const getStatusIcon = () => {
        if (operationalSettings?.useInvestmentOpenTime == 0) {
            return <TrendingUp />;
        }
        
        if (isInvestmentOpen && countdownActive) {
            return <TrendingUp />;
        } else {
            return <PauseCircleOutline />;
        }
    };

    if (operationalSettings?.useInvestmentOpenTime == 0) {
        return (
            <Tooltip title={getTooltipMessage()} arrow placement="bottom">
                <Chip
                    icon={<TrendingUp />}
                    label="Always Open"
                    sx={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontWeight: 600,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        height: isMobile ? 32 : 36,
                        '&:hover': {
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.3) 100%)',
                            transform: 'scale(1.05)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            </Tooltip>
        );
    }

    return (
        <Tooltip 
            title={getTooltipMessage()} 
            arrow 
            placement="bottom"
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 300 }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '6px 12px',
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${getStatusColor()}15 0%, ${getStatusColor()}25 100%)`,
                    border: `1px solid ${getStatusColor()}40`,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 8px 25px ${getStatusColor()}30`,
                        background: `linear-gradient(135deg, ${getStatusColor()}20 0%, ${getStatusColor()}30 100%)`,
                    },
                    minWidth: isMobile ? '120px' : '140px',
                }}
            >
                <AccessTime 
                    sx={{ 
                        color: getStatusColor(), 
                        fontSize: isMobile ? '16px' : '18px',
                        animation: countdownActive ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.6 },
                            '100%': { opacity: 1 }
                        }
                    }} 
                />
                
                {countdownActive ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: getStatusColor(),
                                fontWeight: 700,
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                fontFamily: 'monospace',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
                        </Typography>
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            color: getStatusColor(),
                            fontWeight: 600,
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                        }}
                    >
                        Closed
                    </Typography>
                )}
                
                {getStatusIcon()}
            </Box>
        </Tooltip>
    );
};

export default InvestmentCountdown;
